// src/services/languageAPI.js
//
// The signed-in user's preferred language: GET / PATCH /api/me/language
// → { preferredLanguage: "en" | "ml" | "hi" } (backend PR #337, V197).
//
// Deliberately a bare fetch, not the axios client in api.js, for the same
// reason as chatTranslateAPI.js: that client's 401/403 interceptor logs the
// user out, and a language call must never do that. The window.fetch session
// guard (utils/sessionExpiry.js) would do the same, so this path is on its
// NEVER_ENDS_SESSION list. Until #337 is deployed the endpoint 404s; every
// failure — any non-2xx, a network error, a body that isn't JSON, or no answer
// within 8 s — comes back as { ok:false, status } and never rejects, so the
// caller decides what to show.

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const TIMEOUT_MS = 8000;

export const LANGUAGES = ['en', 'ml', 'hi'];

export const isLanguage = (lang) => LANGUAGES.includes(lang);

async function call(method, body) {
  const jwt = localStorage.getItem('previu_token');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/me/language`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      return { ok: false, status: response.status, message: data?.message || null };
    }
    // A missing or unknown value reads as English, as the backend stores it.
    const lang = data?.preferredLanguage;
    return { ok: true, preferredLanguage: isLanguage(lang) ? lang : 'en' };
  } catch (err) {
    return { ok: false, status: 0, reason: err?.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK' };
  } finally {
    clearTimeout(timer);
  }
}

/** @returns {Promise<{ok:boolean, preferredLanguage?:string, status?:number}>}  never rejects */
export const getLanguage = () => call('GET');

/**
 * @param {'en'|'ml'|'hi'} lang
 * @returns {Promise<{ok:boolean, preferredLanguage?:string, status?:number, message?:string}>}  never rejects
 */
export const setLanguage = (lang) => call('PATCH', { preferredLanguage: lang });
